import React from 'react';
import { Loader2 } from 'lucide-react';

export interface CountAllProgressModalProps {
  show: boolean;
  countAllComponentProgress: Array<{
    name: string;
    status: 'pending' | 'processing' | 'complete' | 'failed';
    color: string;
    referenceImage: string;
    totalBatches?: number;
    completedBatches?: number;
    currentPage?: number;
    currentBatch?: number;
    count?: number;
  }>;
}

export const CountAllProgressModal: React.FC<CountAllProgressModalProps> = ({
  show,
  countAllComponentProgress,
}) => {
  if (!show) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100007,
        padding: '20px',
        backdropFilter: 'blur(4px)'
      }}
    >
      <div
        style={{
          background: 'var(--bg-primary)',
          borderRadius: '20px',
          padding: '28px',
          maxWidth: '480px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 1px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.8)'
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
          <h3 style={{ margin: 0, marginBottom: '8px', color: 'var(--text-primary)', fontSize: '18px', fontWeight: 700, letterSpacing: '-0.01em' }}>
            AI Auto-Counting
          </h3>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '13px' }}>
            {(() => {
              const processingComponents = countAllComponentProgress.filter(p => p.status === 'processing');
              if (processingComponents.length > 0 && processingComponents[0].totalBatches) {
                const bpp = (processingComponents[0].totalBatches || 0) > 10 ? 20 : 5;
                const totalPages = Math.ceil((processingComponents[0].totalBatches || 0) / bpp);
                const uniquePages = new Set(processingComponents.map(p => p.currentPage).filter(Boolean));
                return `Scanning ${totalPages} pages in parallel • ${uniquePages.size} active pages`;
              }
              return 'Analyzing components across all pages...';
            })()}
          </p>
        </div>

        {/* Current Component Progress Bar - resets for each component */}
        {(() => {
          const current = countAllComponentProgress.find(p => p.status === 'processing');
          const completedCount = countAllComponentProgress.filter(p => p.status === 'complete').length;
          const totalCount = countAllComponentProgress.length;
          const totalDetections = countAllComponentProgress.reduce((sum, p) => sum + (p.count || 0), 0);

          const totalBatches = current ? (current.totalBatches || 0) : 0;
          const completedBatches = current ? (current.completedBatches || 0) : 0;
          const percentage = totalBatches > 0 ? Math.round((completedBatches / totalBatches) * 100) : 0;

          return (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600 }}>
                  {current ? `Counting: ${current.name}` : (completedCount === totalCount ? 'Complete' : 'Waiting...')}
                </span>
                <span style={{ fontSize: '16px', color: '#3b82f6', fontWeight: 700 }}>
                  {percentage}%
                </span>
              </div>
              <div style={{
                width: '100%',
                height: '10px',
                background: 'var(--border-color)',
                borderRadius: '10px',
                overflow: 'hidden',
                boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{
                  width: `${percentage}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
                  transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  borderRadius: '10px',
                  boxShadow: percentage > 0 ? '0 0 8px rgba(59, 130, 246, 0.4)' : 'none'
                }} />
              </div>
              <div style={{
                marginTop: '6px',
                textAlign: 'center',
                fontSize: '11px',
                color: 'var(--text-placeholder)',
                fontWeight: 500
              }}>
                {totalDetections} detections • {completedCount}/{totalCount} components complete
              </div>
            </div>
          );
        })()}

        {/* Component List - Scrollable */}
        <div
          className="count-all-scrollbar"
          style={{
            maxHeight: '280px',
            overflowY: 'auto',
            overflowX: 'hidden',
            marginTop: '16px',
            paddingRight: '4px'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {countAllComponentProgress.map((item, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 12px',
                  background: item.status === 'processing' ? 'rgba(59, 130, 246, 0.05)' : 'var(--bg-secondary)',
                  borderRadius: '10px',
                  border: item.status === 'processing' ? '1.5px solid #3b82f6' : '1px solid var(--border-color)',
                  transition: 'all 0.3s ease',
                  boxShadow: item.status === 'processing' ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none'
                }}
              >
                {/* Reference Image Thumbnail */}
                <div style={{
                  width: '40px',
                  height: '40px',
                  flexShrink: 0,
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: `2px solid ${item.color}`,
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  background: 'var(--bg-primary)'
                }}>
                  <img
                    src={item.referenceImage}
                    alt={item.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>

                {/* Component Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    fontSize: '13px',
                    marginBottom: '2px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {item.name}
                  </div>

                  {/* Status text with page and batch info */}
                  {item.status === 'processing' && (
                    <div style={{ fontSize: '11px', color: '#3b82f6', fontWeight: 600, marginTop: '2px' }}>
                      {item.currentPage && item.totalBatches ? (
                        <>
                          Pg {item.currentPage}/{Math.ceil(item.totalBatches / ((item.totalBatches || 0) > 10 ? 20 : 5))}
                          {item.currentBatch !== undefined && (
                            <> • B{item.currentBatch}/{(item.totalBatches || 0) > 10 ? 20 : 5}</>
                          )}
                          {item.count !== undefined && item.count > 0 && (
                            <> • {item.count} found</>
                          )}
                        </>
                      ) : (
                        'Processing...'
                      )}
                    </div>
                  )}

                  {item.status === 'complete' && item.count !== undefined && (
                    <div style={{ fontSize: '11px', color: 'var(--green-text)', fontWeight: 600, marginTop: '2px' }}>
                      ✓ Found {item.count}
                    </div>
                  )}

                  {item.status === 'pending' && (
                    <div style={{ fontSize: '11px', color: 'var(--text-placeholder)', fontWeight: 500, marginTop: '2px' }}>
                      Waiting...
                    </div>
                  )}
                </div>

                {/* Status Icon */}
                <div style={{
                  flexShrink: 0,
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {item.status === 'pending' && (
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: 'var(--border-strong)'
                    }} />
                  )}
                  {item.status === 'processing' && (
                    <Loader2 size={18} className="spin" style={{ color: '#3b82f6' }} />
                  )}
                  {item.status === 'complete' && (
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      background: '#059669',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#ffffff',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      ✓
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
